import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Pagination,
  AppBar,
  Autocomplete,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconPrinter } from '@tabler/icons-react';
import NavBar from '../Navigation/Navigation';
import brandList from './brandList.json';
import departmentList from './departmentList.json';
import storeList from './storeList.json';

interface InventoryRow {
  Style_Code: string;
  Location_ID: string;
  Brand: string;
  Department: string;
  Style_Name: string;
  MSRP: number;
  MarkDownDate: string | null;
  StorePrice: number;
  Description_1: string;
  TotalSKUQty: number;
  TotalLTDSoldQty: number;
  SoldQty: number;
  LRDate: string;
  TotalAgedDays: number;
}

const FixedBottomBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  padding: theme.spacing(1),
  zIndex: 1300,
  overflowX: 'auto'
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(10),
  alignItems: 'flex-end',
  justifyContent: 'center'
}));

const formatDateWithTime = (date: Date, hoursOffset: number) => {
  const adjusted = new Date(date);
  adjusted.setHours(adjusted.getHours() + hoursOffset);
  return adjusted.toISOString();
};

const subDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

const handleGenerateReport = async (styleCode: string) => {
  const reportWindow = window.open('', '_blank');
  if (!reportWindow) {
    alert('Failed to open new window. Please check your popup blocker settings.');
    return;
  }

  reportWindow.document.write('Loading report...');

  try {
    const toDateOnly = (dateTime: string) => dateTime.split('T')[0];
    const getStartDate = () => {
      const thirtyDaysAgo = subDays(new Date(), 30);
      return formatDateWithTime(thirtyDaysAgo, 3);
    };

    const queryParams = new URLSearchParams({
      styleCode,
      startDate: toDateOnly(getStartDate()),
      endDate: toDateOnly(new Date().toISOString())
    }).toString();

    const response = await fetch(`/report/pdf?${queryParams}`, {
      method: 'GET'
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      reportWindow.location.href = url;
    } else {
      reportWindow.close();
      alert('Failed to generate report');
    }
  } catch (error) {
    console.error('Error generating report:', error);
    reportWindow.close();
    alert('An error occurred while generating the report.');
  }
};

const AgedInventoryTable: React.FC = () => {
  const [data, setData] = useState<InventoryRow[]>([]);
  const [location, setLocation] = useState('Steeles');
  const [brand, setBrand] = useState('Adidas');
  const [department, setDepartment] = useState('Mens Apparel');

  const [agedFilter, setAgedFilter] = useState('less');
  const [agedValue1, setAgedValue1] = useState<number | null>(90);
  const [agedValue2, setAgedValue2] = useState<number | null>(null);

  const [qtyFilter, setQtyFilter] = useState('less');
  const [qtyValue1, setQtyValue1] = useState<number | null>(100);
  const [qtyValue2, setQtyValue2] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = async (customPage = page) => {
    setLoading(true);
    const body: any = {
      brand,
      department,
      location,
      page: customPage,
      pageSize
    };

    if (agedFilter === 'greater') body.GreaterAged = agedValue1;
    else if (agedFilter === 'less') body.LessAged = agedValue1;
    else if (agedFilter === 'between') {
      body.MinAgedDays = agedValue1;
      body.MaxAgedDays = agedValue2;
    }

    if (qtyFilter === 'greater') body.GreaterOHQty = qtyValue1;
    else if (qtyFilter === 'less') body.LessOHQty = qtyValue1;
    else if (qtyFilter === 'between') {
      body.MinOHQty = qtyValue1;
      body.MaxOHQty = qtyValue2;
    }

    const response = await fetch('/aged/agedInventory', {
      method: 'POST',
      credentials:'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    const result = await response.json();
    setData(result.data);
    setTotalCount(result.totalCount);
    setLoading(false);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <AppBar position="sticky">
        <NavBar />
      </AppBar>
 
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress size={60} thickness={5} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                     <Table stickyHeader>
              <TableHead>
                 <TableRow>
                                  {[
                                    'Location','Style No', 'Description', 'Brand', 'Department', 'Store Price','MSRP',
                                    'Qty', 'Sold Qty', 'LR Date', 'Markdown Date',
                                    'Aged Days',  'Print Read'
                                  ].map((header) => (
                                    <TableCell key={header} sx={{
                                      fontWeight: 'bold',
                                      fontFamily: 'Arial',
                                      fontSize: '11pt',
                                      backgroundColor: '#f5ecc8',
                                      color: 'black',
                                      position: 'sticky',
                                      top: 0,
                                      zIndex: 1
                                    }}>
                                      {header}
                                    </TableCell>
                                  ))}
                                </TableRow>
              
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                    <TableCell>{row.Location_ID}</TableCell>
                    <TableCell>{row.Style_Code}</TableCell>
                    <TableCell>{row.Description_1}</TableCell>
                    <TableCell>{row.Brand}</TableCell>
                    <TableCell>{row.Department}</TableCell>
                    <TableCell>${row.StorePrice}</TableCell>
                    <TableCell>${row.MSRP}</TableCell>
                    <TableCell>{row.TotalSKUQty}</TableCell>
                    <TableCell>{row.SoldQty}</TableCell>
                    <TableCell>{row.LRDate}</TableCell>
                    <TableCell>{row.MarkDownDate}</TableCell>
                    <TableCell>{row.TotalAgedDays}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleGenerateReport(row.Style_Code)}>
                        <IconPrinter size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mb={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  fetchData(value);
                }}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <FixedBottomBar>
        <FilterContainer>
          <Autocomplete
            options={storeList}
            value={location}
            onChange={(_, newValue) => setLocation(newValue || '')}
            renderInput={(params) => <TextField {...params} label="Store" size="small" />}
            sx={{ minWidth: 150 }}
          />

          <Autocomplete
            options={brandList}
            value={brand}
            onChange={(_, newValue) => setBrand(newValue || '')}
            renderInput={(params) => <TextField {...params} label="Brand" size="small" />}
            sx={{ minWidth: 150 }}
          /> 

          <Autocomplete
            options={departmentList}
            value={department}
            onChange={(_, newValue) => setDepartment(newValue || '')}
            renderInput={(params) => <TextField {...params} label="Department" size="small" />}
            sx={{ minWidth: 150 }}
          />

          <Box>
            <Typography variant="caption">Aged Days</Typography>
            <RadioGroup row value={agedFilter} onChange={(e) => setAgedFilter(e.target.value)}>
              <FormControlLabel value="greater" control={<Radio size="small" />} label=">" />
              <FormControlLabel value="less" control={<Radio size="small" />} label="<" />
              <FormControlLabel value="between" control={<Radio size="small" />} label="Between" />
            </RadioGroup>
            <Box display="flex" gap={1}>
              <TextField
                type="number"
                size="small"
                label="Value"
                value={agedValue1 ?? ''}
                onChange={(e) => setAgedValue1(Number(e.target.value))}
              />
              {agedFilter === 'between' && (
                <TextField
                  type="number"
                  size="small"
                  label="To"
                  value={agedValue2 ?? ''}
                  onChange={(e) => setAgedValue2(Number(e.target.value))}
                />
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="caption">Qty</Typography>
            <RadioGroup row value={qtyFilter} onChange={(e) => setQtyFilter(e.target.value)}>
              <FormControlLabel value="greater" control={<Radio size="small" />} label=">" />
              <FormControlLabel value="less" control={<Radio size="small" />} label="<" />
              <FormControlLabel value="between" control={<Radio size="small" />} label="Between" />
            </RadioGroup>
            <Box display="flex" gap={1}>
              <TextField
                type="number"
                size="small"
                label="Value"
                value={qtyValue1 ?? ''}
                onChange={(e) => setQtyValue1(Number(e.target.value))}
              />
              {qtyFilter === 'between' && (
                <TextField
                  type="number"
                  size="small"
                  label="To"
                  value={qtyValue2 ?? ''}
                  onChange={(e) => setQtyValue2(Number(e.target.value))}
                />
              )}
            </Box>
          </Box>

          <Button variant="contained" size="medium" onClick={() => { setPage(1); fetchData(1); }}>
            Apply
          </Button>
        </FilterContainer>
      </FixedBottomBar>
    </>
  );
};

export default AgedInventoryTable;